import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChild } from '@fortawesome/pro-solid-svg-icons/faChild';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faHouse } from '@fortawesome/pro-solid-svg-icons/faHouse';

import {
  EmailNotificationType,
  SavedSearch,
  SavedSearchService,
  SitterSearchNotificationTypeService,
} from 'us-web-services';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { getLongDateString } from 'us-react-components/dist/utils/date';

import { Button, ButtonSize, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

type VerticalCode = 'CHILD_CARE' | 'SENIOR_CARE' | 'PET_CARE' | 'HOUSEHOLD_SERVICES';

export const SavedSearches = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const parent = stores.parent.parent;

  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [notificationTypes, setNotificationTypes] = useState<EmailNotificationType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [closingId, setClosingId] = useState<number | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [closeSubmitting, setCloseSubmitting] = useState(false);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterSearchNotificationTypeService.get();
        setNotificationTypes(resp.data?.data ?? []);
      } catch {
        setNotificationTypes([]);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    document.title = 'UrbanSitter - Saved Searches';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await SavedSearchService.getSavedSearchByFilter({
          params: { active: true, page: 1, parentId: user.id },
        });
        if (cancelled) return;
        setSearches(resp.data?.data ?? []);
      } catch (err) {
        console.warn('[SavedSearches] load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [user?.id]);

  const openClose = (id: number): void => {
    setClosingId(id);
    setCloseReason('');
  };

  const confirmClose = async (): Promise<void> => {
    if (!closingId) return;
    setCloseSubmitting(true);
    try {
      await SavedSearchService.patch(closingId, {
        active: false,
        closeReason: closeReason || 'other',
      } as never);
      setSearches((prev) => prev.filter((s) => s.id !== closingId));
      toast.success(t('pages.savedSearches.closeSuccess'));
      setClosingId(null);
    } catch {
      toast.error(t('pages.savedSearches.closeError'));
    } finally {
      setCloseSubmitting(false);
    }
  };

  const setNotificationType = async (searchId: number, typeId: number): Promise<void> => {
    const type = notificationTypes.find((nt) => nt.id === typeId);
    if (!type) return;
    try {
      await SavedSearchService.patch(searchId, { notificationType: type } as never);
      setSearches((prev) =>
        prev.map((s) =>
          s.id === searchId
            ? ({ ...s, notificationType: type } as unknown as SavedSearch)
            : s,
        ),
      );
    } catch {
      toast.error(t('pages.savedSearches.notificationsError'));
    }
  };

  const launchSearch = (code: VerticalCode): void => {
    const params = new URLSearchParams();
    params.set('verticalTypeId', String(bookingVerticalType[code]));
    if (code === 'PET_CARE') {
      if (parent.dog) params.set('willSitDog', 'true');
      if (parent.cat) params.set('willSitCat', 'true');
      if (parent.otherPet) params.set('willSitOtherPet', 'true');
    } else if (code === 'SENIOR_CARE') {
      params.set('willSitSenior', 'true');
    } else if (code === 'HOUSEHOLD_SERVICES') {
      params.set('willSitHome', 'true');
    }
    navigate(`/sitters?${params.toString()}`);
  };

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-6'>{t('pages.savedSearches.heading')}</h1>
      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4'>
        <div className='sm:col-span-7 md:col-span-8'>
          <div className='mb-3'>
            <h3 className='text-lg font-semibold'>{t('pages.savedSearches.openSearchesHeading')}</h3>
          </div>

          {isLoading && (
            <div className='text-neutral-600 text-center pt-4'>
              <i className='fa-solid fa-spinner fa-spin mr-2' />
              <span>{t('common.loading')}</span>
            </div>
          )}

          {!isLoading && searches.length === 0 && (
            <div id='null-state' className='us-card p-6 rounded pb-16'>
              <div className='font-semibold mb-2'>{t('pages.savedSearches.emptyHeading')}</div>
              {t('pages.savedSearches.emptyBody')}
            </div>
          )}

          {!isLoading && searches.map((s) => (
            <div key={s.id} className='us-card rounded mb-3 p-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <div>
                  <h2 className='mb-0 text-xl'>
                    <a
                      id={`go-to-saved-search-${s.id}`}
                      href={`/app/sitters?savedSearchId=${s.id}`}
                      className='underline'
                    >
                      {s.title}
                    </a>
                  </h2>
                  <p>{t('pages.savedSearches.savedOn', { date: getLongDateString(s.modified) })}</p>
                  <div>
                    <span className='font-semibold'>
                      {t('pages.savedSearches.filters', { count: s.filterCount ?? 0 })}
                    </span>
                  </div>
                  <div className='mt-2'>
                    <label htmlFor={`notifications-${s.id}`} className='text-sm block mb-1'>
                      {t('pages.savedSearches.emailNotifications')}
                    </label>
                    <select
                      id={`notifications-${s.id}`}
                      value={(s.notificationType?.id ?? '') as number | ''}
                      onChange={(e): Promise<void> =>
                        setNotificationType(s.id as number, Number(e.target.value))
                      }
                      className='form-control border border-neutral-300 rounded px-2 py-1 text-sm'
                    >
                      {notificationTypes.length === 0 && (
                        <option value=''>{t('pages.savedSearches.notificationsLoading')}</option>
                      )}
                      {notificationTypes.map((nt) => (
                        <option key={nt.id} value={nt.id}>
                          {nt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className='flex flex-col gap-2'>
                  <a
                    id={`view-search-${s.id}`}
                    className='btn btn-secondary w-full inline-block text-center'
                    href={`/app/sitters?savedSearchId=${s.id}`}
                  >
                    {t('pages.savedSearches.viewSearch')}
                  </a>
                  <Button
                    buttonType={ButtonType.tertiary}
                    id={`close-search-${s.id}`}
                    className='w-full'
                    onClick={(): void => openClose(s.id as number)}
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='sm:col-span-5 md:col-span-4'>
          <h4 className='mb-0 font-semibold'>{t('pages.savedSearches.startNewHeading')}</h4>
          <div className='us-card rounded p-4 mt-2'>
            <div className='grid grid-cols-1 mb-3'>
              <Button
                buttonType={ButtonType.secondary}
                id='all-sitters-search'
                onClick={(): void => launchSearch('CHILD_CARE')}
                className='cursor-pointer text-center py-3 font-semibold w-full'
              >
                <FontAwesomeIcon icon={faChild} className='text-sapphire text-3xl mr-2 align-middle' />
                {t('pages.savedSearches.typeChild')}
              </Button>
            </div>
            <div className='grid grid-cols-3 gap-2'>
              <Button
                buttonType={ButtonType.secondary}
                buttonSize={ButtonSize.small}
                id='senior-caregiver-search'
                onClick={(): void => launchSearch('SENIOR_CARE')}
                className='cursor-pointer text-center py-3 font-semibold w-full'
              >
                <FontAwesomeIcon icon={faHeart} className='block text-sapphire text-3xl mx-auto mb-1' />
                <span className='block leading-tight'>{t('pages.savedSearches.typeSeniorLine1')}</span>
                <span className='block leading-tight'>{t('pages.savedSearches.typeSeniorLine2')}</span>
              </Button>
              <Button
                buttonType={ButtonType.secondary}
                buttonSize={ButtonSize.small}
                id='pet-sitters-search'
                onClick={(): void => launchSearch('PET_CARE')}
                className='cursor-pointer text-center py-3 font-semibold w-full'
              >
                <FontAwesomeIcon icon={faPaw} className='block text-sapphire text-3xl mx-auto mb-1' />
                <span className='block leading-tight'>{t('pages.savedSearches.typePetLine1')}</span>
                <span className='block leading-tight'>{t('pages.savedSearches.typePetLine2')}</span>
              </Button>
              <Button
                buttonType={ButtonType.secondary}
                buttonSize={ButtonSize.small}
                id='household-caregiver-search'
                onClick={(): void => launchSearch('HOUSEHOLD_SERVICES')}
                className='cursor-pointer text-center py-3 font-semibold w-full'
              >
                <FontAwesomeIcon icon={faHouse} className='block text-sapphire text-3xl mx-auto mb-1' />
                <span className='block leading-tight'>{t('pages.savedSearches.typeHouseholdLine1')}</span>
                <span className='block leading-tight'>{t('pages.savedSearches.typeHouseholdLine2')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={closingId != null}
        onClose={(): void => setClosingId(null)}
        title={t('pages.savedSearches.deleteModalTitle')}
        className='w-full max-w-md'
        footer={
          <>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => setClosingId(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              id='saved-search-close-modal-confirm'
              buttonType={ButtonType.primary}
              disabled={closeSubmitting}
              onClick={confirmClose}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <select
          className='form-control w-full border border-neutral-300 rounded px-3 py-2'
          value={closeReason}
          onChange={(e): void => setCloseReason(e.target.value)}
        >
          <option value=''>{t('pages.savedSearches.reasonPrompt')}</option>
          <option value='found_caregiver'>{t('pages.savedSearches.reasonFoundCaregiver')}</option>
          <option value='no_longer_need'>{t('pages.savedSearches.reasonNoLongerNeed')}</option>
          <option value='too_many_emails'>{t('pages.savedSearches.reasonTooManyEmails')}</option>
          <option value='other'>{t('pages.savedSearches.reasonOther')}</option>
        </select>
      </Modal>
    </div>
  );
});
